import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Cannabis, MapPin, Phone, Globe, Clock, Truck, Store, ExternalLink, ShieldAlert, Plus, Sprout, Leaf, Home, Sun, Warehouse, Package, Scissors, FlaskConical, Droplets, Tag, Star, CheckCircle, Heart, Feather, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import { useAuth } from '@/context/AuthContext';
import { OwnershipBadges } from '@/components/OwnershipBadges';

const API = "/api";

const AGE_VERIFIED_KEY = 'twintube_canna_age_verified';
const SPLASH_SEEN_KEY = 'twintube_canna_splash_seen';
const WELCOME_IMAGE = 'https://customer-assets.emergentagent.com/job_twintube/artifacts/p3xfilz4_Image_fx%20%2816%29.png%20%288%29.jpg';

const CannaClub = () => {
  const { token } = useAuth();
  const [ageVerified, setAgeVerified] = useState(false);
  const [showAgeModal, setShowAgeModal] = useState(true);
  const [showWelcomeSplash, setShowWelcomeSplash] = useState(false);
  const [dispensaries, setDispensaries] = useState([]);
  const [featuredDispensaries, setFeaturedDispensaries] = useState([]);
  const [growers, setGrowers] = useState([]);
  const [featuredGrowers, setFeaturedGrowers] = useState([]);
  const [extractors, setExtractors] = useState([]);
  const [featuredExtractors, setFeaturedExtractors] = useState([]);
  const [growerSpotlight, setGrowerSpotlight] = useState(null);
  const [extractorSpotlight, setExtractorSpotlight] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [cityFilter, setCityFilter] = useState('all');
  const [ownershipFilter, setOwnershipFilter] = useState([]);
  const [activeTab, setActiveTab] = useState('dispensaries');

  useEffect(() => {
    // Check if user already verified age
    const verified = localStorage.getItem(AGE_VERIFIED_KEY);
    if (verified === 'true') {
      setAgeVerified(true);
      setShowAgeModal(false);
      fetchData();
    }
  }, []);

  const handleAgeVerification = (isOver21) => {
    if (isOver21) {
      localStorage.setItem(AGE_VERIFIED_KEY, 'true');
      setAgeVerified(true);
      setShowAgeModal(false);
      // Check if splash was already seen this session
      const splashSeen = sessionStorage.getItem(SPLASH_SEEN_KEY);
      if (!splashSeen) {
        setShowWelcomeSplash(true);
      } else {
        fetchData();
      }
    } else {
      // Redirect away if under 21
      window.location.href = '/';
    }
  };

  const handleCloseSplash = () => {
    sessionStorage.setItem(SPLASH_SEEN_KEY, 'true');
    setShowWelcomeSplash(false);
    fetchData();
  };

  const fetchData = async () => {
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    try {
      const [
        dispensariesRes, featuredDispRes, 
        growersRes, featuredGrowersRes, growerSpotlightRes,
        extractorsRes, featuredExtractorsRes, extractorSpotlightRes
      ] = await Promise.all([
        axios.get(`${API}/dispensaries`, { headers }),
        axios.get(`${API}/dispensaries/featured`, { headers }),
        axios.get(`${API}/growers`, { headers }),
        axios.get(`${API}/growers/featured`, { headers }),
        axios.get(`${API}/growers/spotlight`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/extractors`, { headers }),
        axios.get(`${API}/extractors/featured`, { headers }),
        axios.get(`${API}/extractors/spotlight`, { headers }).catch(() => ({ data: null }))
      ]);
      setDispensaries(dispensariesRes.data);
      setFeaturedDispensaries(featuredDispRes.data);
      setGrowers(growersRes.data);
      setFeaturedGrowers(featuredGrowersRes.data);
      setGrowerSpotlight(growerSpotlightRes.data);
      setExtractors(extractorsRes.data);
      setFeaturedExtractors(featuredExtractorsRes.data);
      setExtractorSpotlight(extractorSpotlightRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredDispensaries = dispensaries.filter(d => {
    const matchesSearch = d.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         d.description?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCity = cityFilter === 'all' || d.city === cityFilter;
    return matchesSearch && matchesCity;
  });

  const matchesOwnership = (item) => {
    if (ownershipFilter.length === 0) return true;
    return ownershipFilter.every(f => {
      if (f === 'women') return item.women_owned;
      if (f === 'native') return item.native_owned;
      if (f === 'bipoc') return item.bipoc_owned;
      return true;
    });
  };

  const filteredGrowers = growers.filter(g => {
    const matchesSearch = g.business_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         g.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         g.specialties?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCity = cityFilter === 'all' || g.city === cityFilter;
    return matchesSearch && matchesCity && matchesOwnership(g);
  });

  const filteredExtractors = extractors.filter(e => {
    const matchesSearch = e.business_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         e.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         e.extraction_types?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         e.products?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCity = cityFilter === 'all' || e.city === cityFilter;
    return matchesSearch && matchesCity && matchesOwnership(e);
  });

  const toggleOwnershipFilter = (filter) => {
    setOwnershipFilter(prev => 
      prev.includes(filter) ? prev.filter(f => f !== filter) : [...prev, filter]
    );
  };

  const allCities = [...new Set([
    ...dispensaries.map(d => d.city),
    ...growers.map(g => g.city),
    ...extractors.map(e => e.city)
  ])].filter(Boolean).sort();

  // Age Verification Modal
  if (!ageVerified) {
    return (
      <Dialog open={showAgeModal} onOpenChange={() => {}}>
        <DialogContent className="sm:max-w-md bg-background border-green-500/30" hideClose>
          <DialogHeader className="text-center">
            <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4">
              <ShieldAlert size={32} className="text-green-500" />
            </div>
            <DialogTitle className="text-2xl font-heading">Age Verification Required</DialogTitle>
            <DialogDescription className="text-base">
              You must be 21 years or older to access the Canna-Club section.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-6">
            <p className="text-center text-muted-foreground mb-6">
              By entering, you confirm that you are at least 21 years of age and agree to our terms of service.
            </p>
            
            <div className="flex flex-col gap-3">
              <Button 
                onClick={() => handleAgeVerification(true)}
                className="w-full bg-green-600 hover:bg-green-700"
                size="lg"
                data-testid="age-verify-yes"
              >
                Yes, I am 21 or older
              </Button>
              <Button 
                onClick={() => handleAgeVerification(false)}
                variant="outline"
                className="w-full"
                size="lg"
                data-testid="age-verify-no"
              >
                No, I am under 21
              </Button>
            </div>
          </div>
          
          <p className="text-xs text-center text-muted-foreground">
            Cannabis is for adults 21+ only. Please consume responsibly.
          </p>
        </DialogContent>
      </Dialog>
    );
  }

  // Welcome Splash Screen
  if (showWelcomeSplash) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black"
        onClick={handleCloseSplash}
      >
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.5 }}
          className="relative max-w-2xl w-full mx-4 text-center"
        >
          <motion.img
            src={WELCOME_IMAGE}
            alt="Welcome to the Club"
            className="w-full max-w-md mx-auto rounded-lg shadow-2xl shadow-green-500/30 mb-8"
            initial={{ y: -20 }}
            animate={{ y: 0 }}
            transition={{ delay: 0.3, duration: 0.5 }}
          />
          
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.5, duration: 0.5 }}
          >
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-green-400 mb-4">
              Welcome to the Club!
            </h1>
            <p className="text-xl md:text-2xl text-white/70 mb-8">
              Minnesota's Cannabis Community
            </p>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8 }}
          >
            <Button 
              onClick={handleCloseSplash}
              className="bg-green-600 hover:bg-green-700 text-lg px-8 py-6"
              size="lg"
            >
              Enter Canna-Club
            </Button>
            <p className="text-sm text-white/50 mt-4">Click anywhere to continue</p>
          </motion.div>
        </motion.div>
      </motion.div>
    );
  }

  return (
    <div className="space-y-6" data-testid="canna-club-page">
      {/* Hero Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative overflow-hidden rounded-sm bg-gradient-to-br from-green-900/50 via-background to-green-800/30 border border-green-500/30 p-6 md:p-8"
      >
        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 md:w-14 md:h-14 rounded-sm bg-green-500/20 flex items-center justify-center">
              <Cannabis size={24} className="text-green-500" />
            </div>
            <div>
              <h1 className="font-heading text-2xl md:text-4xl font-bold text-green-400">Canna-Club</h1>
              <p className="text-sm md:text-base text-muted-foreground">Minnesota Cannabis Community</p>
            </div>
          </div>
          <p className="text-sm md:text-lg text-muted-foreground max-w-2xl mb-6">
            Discover licensed cannabis businesses in Minnesota - dispensaries, growers, and extractors.
          </p>
          <div className="flex flex-wrap gap-2 md:gap-3">
            <Button asChild className="bg-green-600 hover:bg-green-700" size="sm">
              <Link to="/register/dispensary" data-testid="register-dispensary-btn">
                <Store size={16} className="mr-2" /> Dispensary
              </Link>
            </Button>
            <Button asChild variant="outline" className="border-green-500/50 hover:bg-green-500/10" size="sm">
              <Link to="/register/grower" data-testid="register-grower-btn">
                <Sprout size={16} className="mr-2" /> Grower
              </Link>
            </Button>
            <Button asChild variant="outline" className="border-purple-500/50 hover:bg-purple-500/10" size="sm">
              <Link to="/register/extractor" data-testid="register-extractor-btn">
                <FlaskConical size={16} className="mr-2" /> Extractor
              </Link>
            </Button>
          </div>
        </div>
        
        {/* Decorative elements */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-green-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-1/2 w-48 h-48 bg-green-600/10 rounded-full blur-2xl" />
      </motion.div>

      {/* Spotlight Section - Only shows if there's a spotlight set */}
      {(growerSpotlight || extractorSpotlight) && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="grid grid-cols-1 md:grid-cols-2 gap-4"
        >
          {growerSpotlight && (
            <SpotlightCard 
              item={growerSpotlight} 
              type="grower" 
              linkTo={`/grower/${growerSpotlight.id}`}
            />
          )}
          {extractorSpotlight && (
            <SpotlightCard 
              item={extractorSpotlight} 
              type="extractor" 
              linkTo={`/extractor/${extractorSpotlight.id}`}
            />
          )}
        </motion.div>
      )}

      {/* Tabs for Dispensaries/Growers/Extractors */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          <TabsList className="bg-card border border-border">
            <TabsTrigger value="dispensaries" className="data-[state=active]:bg-green-600 data-[state=active]:text-white" data-testid="tab-dispensaries">
              <Store size={16} className="mr-2" />
              Dispensaries
            </TabsTrigger>
            <TabsTrigger value="growers" className="data-[state=active]:bg-green-600 data-[state=active]:text-white" data-testid="tab-growers">
              <Sprout size={16} className="mr-2" />
              Growers
            </TabsTrigger>
            <TabsTrigger value="extractors" className="data-[state=active]:bg-purple-600 data-[state=active]:text-white" data-testid="tab-extractors">
              <FlaskConical size={16} className="mr-2" />
              Extractors
            </TabsTrigger>
          </TabsList>
          
          {/* Search & Filter */}
          <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
            <Input
              placeholder={`Search ${activeTab}...`}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="bg-card border-border w-full sm:w-64"
              data-testid="search-input"
            />
            <Select value={cityFilter} onValueChange={setCityFilter}>
              <SelectTrigger className="w-full sm:w-40" data-testid="city-filter">
                <SelectValue placeholder="All Cities" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Cities</SelectItem>
                {allCities.map(city => (
                  <SelectItem key={city} value={city}>{city}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Ownership Badge Filters */}
        {(activeTab === 'growers' || activeTab === 'extractors') && (
          <div className="flex flex-wrap items-center gap-2 mt-3" data-testid="ownership-filters">
            <span className="text-xs text-muted-foreground mr-1">Shop Local:</span>
            <button
              onClick={() => toggleOwnershipFilter('women')}
              data-testid="filter-women-owned"
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                ownershipFilter.includes('women')
                  ? 'bg-pink-500/30 text-pink-300 border border-pink-500/50'
                  : 'bg-card border border-border text-muted-foreground hover:border-pink-500/40 hover:text-pink-400'
              }`}
            >
              <Heart size={12} /> Women Owned
            </button>
            <button
              onClick={() => toggleOwnershipFilter('native')}
              data-testid="filter-native-owned"
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                ownershipFilter.includes('native')
                  ? 'bg-orange-500/30 text-orange-300 border border-orange-500/50'
                  : 'bg-card border border-border text-muted-foreground hover:border-orange-500/40 hover:text-orange-400'
              }`}
            >
              <Feather size={12} /> Native Owned
            </button>
            <button
              onClick={() => toggleOwnershipFilter('bipoc')}
              data-testid="filter-bipoc-owned"
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                ownershipFilter.includes('bipoc')
                  ? 'bg-amber-500/30 text-amber-300 border border-amber-500/50'
                  : 'bg-card border border-border text-muted-foreground hover:border-amber-500/40 hover:text-amber-400'
              }`}
            >
              <Users size={12} /> BIPOC Owned
            </button>
            {ownershipFilter.length > 0 && (
              <button
                onClick={() => setOwnershipFilter([])}
                className="text-xs text-muted-foreground hover:text-white underline ml-1"
                data-testid="clear-ownership-filters"
              >
                Clear filters
              </button>
            )}
          </div>
        )}

        {/* Dispensaries Tab */}
        <TabsContent value="dispensaries" className="mt-6 space-y-6">
          {featuredDispensaries.length > 0 && (
            <motion.section initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
              <h2 className="font-heading text-xl md:text-2xl font-bold mb-4 flex items-center gap-2">
                <Cannabis className="text-green-500" size={24} />
                Featured Dispensaries
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                {featuredDispensaries.map(dispensary => (
                  <DispensaryCard key={dispensary.id} dispensary={dispensary} featured />
                ))}
              </div>
            </motion.section>
          )}

          <motion.section initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
            <h2 className="font-heading text-xl md:text-2xl font-bold mb-4">All Dispensaries</h2>
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-64 rounded-sm" />)}
              </div>
            ) : filteredDispensaries.length === 0 ? (
              <EmptyState type="dispensaries" />
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                {filteredDispensaries.map(dispensary => (
                  <DispensaryCard key={dispensary.id} dispensary={dispensary} />
                ))}
              </div>
            )}
          </motion.section>
        </TabsContent>

        {/* Growers Tab */}
        <TabsContent value="growers" className="mt-6 space-y-6">
          {featuredGrowers.length > 0 && (
            <motion.section initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
              <h2 className="font-heading text-xl md:text-2xl font-bold mb-4 flex items-center gap-2">
                <Sprout className="text-green-500" size={24} />
                Featured Growers
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                {featuredGrowers.map(grower => <GrowerCard key={grower.id} grower={grower} featured />)}
              </div>
            </motion.section>
          )}

          <motion.section initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
            <h2 className="font-heading text-xl md:text-2xl font-bold mb-4">All Growers</h2>
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-64 rounded-sm" />)}
              </div>
            ) : filteredGrowers.length === 0 ? (
              <EmptyState type="growers" />
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                {filteredGrowers.map(grower => <GrowerCard key={grower.id} grower={grower} />)}
              </div>
            )}
          </motion.section>

          <InfoBanner type="grower" />
        </TabsContent>

        {/* Extractors Tab */}
        <TabsContent value="extractors" className="mt-6 space-y-6">
          {featuredExtractors.length > 0 && (
            <motion.section initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
              <h2 className="font-heading text-xl md:text-2xl font-bold mb-4 flex items-center gap-2">
                <FlaskConical className="text-purple-500" size={24} />
                Featured Extractors
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                {featuredExtractors.map(extractor => <ExtractorCard key={extractor.id} extractor={extractor} featured />)}
              </div>
            </motion.section>
          )}

          <motion.section initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
            <h2 className="font-heading text-xl md:text-2xl font-bold mb-4">All Extractors</h2>
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-64 rounded-sm" />)}
              </div>
            ) : filteredExtractors.length === 0 ? (
              <EmptyState type="extractors" />
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                {filteredExtractors.map(extractor => <ExtractorCard key={extractor.id} extractor={extractor} />)}
              </div>
            )}
          </motion.section>

          <InfoBanner type="extractor" />
        </TabsContent>
      </Tabs>

      {/* Info Banner for Dispensaries */}
      {activeTab === 'dispensaries' && <InfoBanner type="dispensary" />}
    </div>
  );
};

// Spotlight Card Component
const SpotlightCard = ({ item, type, linkTo }) => {
  const isGrower = type === 'grower';
  const Icon = isGrower ? Sprout : FlaskConical;
  const colorClass = isGrower ? 'green' : 'purple';
  
  return (
    <Link to={linkTo}>
      <Card className={`bg-gradient-to-br from-${colorClass}-500/10 to-${colorClass}-600/5 border-${colorClass}-500/30 hover:border-${colorClass}-500/50 transition-all cursor-pointer`}>
        <CardContent className="p-4 flex items-center gap-4">
          <div className={`w-16 h-16 rounded-lg bg-${colorClass}-500/20 flex items-center justify-center shrink-0`}>
            {item.image_url ? (
              <img src={item.image_url} alt={item.business_name} className="w-full h-full object-cover rounded-lg" />
            ) : (
              <Icon size={32} className={`text-${colorClass}-500`} />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <Star size={14} className="text-yellow-500" />
              <span className="text-xs text-yellow-500 font-medium">SPOTLIGHT</span>
            </div>
            <h3 className="font-bold truncate">{item.business_name}</h3>
            <p className="text-sm text-muted-foreground flex items-center gap-1">
              <MapPin size={12} /> {item.city}, MN
            </p>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
};

// Empty State Component
const EmptyState = ({ type }) => {
  const config = {
    dispensaries: { icon: Cannabis, link: '/register/dispensary', label: 'dispensary' },
    growers: { icon: Sprout, link: '/register/grower', label: 'grower' },
    extractors: { icon: FlaskConical, link: '/register/extractor', label: 'extractor' }
  };
  const { icon: Icon, link, label } = config[type];
  
  return (
    <div className="text-center py-12 bg-card/50 rounded-sm border border-border">
      <Icon size={48} className="mx-auto mb-4 text-muted-foreground" />
      <p className="text-muted-foreground mb-4">No {type} registered yet.</p>
      <Button asChild variant="outline">
        <Link to={link}>Register as {label}</Link>
      </Button>
    </div>
  );
};

// Info Banner Component
const InfoBanner = ({ type }) => {
  const config = {
    dispensary: { 
      title: 'Are you a licensed dispensary?', 
      desc: 'Join the Twin Tube Canna-Club and reach thousands of local cannabis enthusiasts.',
      link: '/register/dispensary',
      color: 'green'
    },
    grower: { 
      title: 'Are you a cannabis grower in Minnesota?', 
      desc: 'Join the Canna-Club grower network and connect with local businesses.',
      link: '/register/grower',
      color: 'green'
    },
    extractor: { 
      title: 'Are you an extraction business?', 
      desc: 'Join the Canna-Club extractor network and connect with dispensaries looking for quality products.',
      link: '/register/extractor',
      color: 'purple'
    }
  };
  const { title, desc, link, color } = config[type];
  
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 0.3 }}
      className={`p-6 bg-${color}-500/10 border border-${color}-500/30 rounded-sm`}
    >
      <h3 className={`font-heading text-lg font-bold mb-2 text-${color}-400`}>{title}</h3>
      <p className="text-sm text-muted-foreground mb-4">{desc}</p>
      <Button asChild className={`bg-${color}-600 hover:bg-${color}-700`}>
        <Link to={link}>Register Now</Link>
      </Button>
    </motion.div>
  );
};

// Dispensary Card Component
const DispensaryCard = ({ dispensary, featured = false }) => (
  <motion.div
    whileHover={{ scale: 1.02 }}
    className={`group relative rounded-sm overflow-hidden bg-card border ${
      featured ? 'border-green-500/50 ring-1 ring-green-500/30' : 'border-border'
    } card-hover`}
    data-testid={`dispensary-card-${dispensary.id}`}
  >
    <div className="aspect-video relative bg-green-900/20">
      {dispensary.image_url ? (
        <img src={dispensary.image_url} alt={dispensary.name} className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full flex items-center justify-center">
          <Cannabis size={48} className="text-green-500/50" />
        </div>
      )}
      {featured && (
        <div className="absolute top-3 right-3 bg-green-500 text-black px-2 py-1 rounded-sm">
          <span className="font-bold text-xs">FEATURED</span>
        </div>
      )}
      <div className="absolute bottom-3 left-3 flex gap-2">
        {dispensary.offers_pickup && (
          <div className="bg-black/70 backdrop-blur-sm px-2 py-1 rounded-sm flex items-center gap-1">
            <Store size={12} /><span className="text-xs">Pickup</span>
          </div>
        )}
        {dispensary.offers_delivery && (
          <div className="bg-black/70 backdrop-blur-sm px-2 py-1 rounded-sm flex items-center gap-1">
            <Truck size={12} /><span className="text-xs">Delivery</span>
          </div>
        )}
      </div>
    </div>
    <div className="p-4">
      <h3 className="font-heading font-bold text-lg mb-1 truncate">{dispensary.name}</h3>
      <div className="flex items-center gap-1 text-sm text-muted-foreground mb-2">
        <MapPin size={14} /><span className="truncate">{dispensary.city}, MN</span>
      </div>
      {dispensary.description && <p className="text-sm text-muted-foreground line-clamp-2 mb-3">{dispensary.description}</p>}
      <div className="flex gap-2 flex-wrap">
        {dispensary.website && (
          <a href={dispensary.website} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-green-400 hover:text-green-300">
            <Globe size={12} /> Website
          </a>
        )}
        {dispensary.menu_url && (
          <a href={dispensary.menu_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-green-400 hover:text-green-300">
            <ExternalLink size={12} /> Menu
          </a>
        )}
        {dispensary.phone && (
          <a href={`tel:${dispensary.phone}`} className="inline-flex items-center gap-1 text-xs text-green-400 hover:text-green-300">
            <Phone size={12} /> Call
          </a>
        )}
      </div>
    </div>
  </motion.div>
);

// Grower Card Component  
const GrowerCard = ({ grower, featured = false }) => {
  const getGrowTypeIcon = (type) => {
    switch (type) {
      case 'indoor': return Home;
      case 'outdoor': return Sun;
      case 'greenhouse': return Warehouse;
      default: return Sprout;
    }
  };
  const GrowTypeIcon = getGrowTypeIcon(grower.grow_type);

  return (
    <Link to={`/grower/${grower.id}`} data-testid={`grower-card-${grower.id}`}>
      <motion.div
        whileHover={{ scale: 1.02 }}
        className={`group relative rounded-sm overflow-hidden bg-card border ${
          featured ? 'border-green-500/50 ring-1 ring-green-500/30' : 'border-border'
        } card-hover cursor-pointer`}
      >
        <div className="aspect-video relative bg-green-900/20">
          {grower.image_url ? (
            <img 
              src={grower.image_url} 
              alt={grower.business_name} 
              className="w-full h-full object-cover"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
          ) : null}
          <div 
            className={`w-full h-full items-center justify-center bg-gradient-to-br from-green-900/30 to-green-600/10 ${grower.image_url ? 'hidden' : 'flex'}`}
            style={{ display: grower.image_url ? 'none' : 'flex' }}
          >
            <Leaf size={48} className="text-green-500/50" />
          </div>
          {featured && (
            <div className="absolute top-3 right-3 bg-green-500 text-black px-2 py-1 rounded-sm">
              <span className="font-bold text-xs">FEATURED</span>
            </div>
          )}
          {grower.is_verified && (
            <div className="absolute top-3 left-3 bg-blue-500/90 text-white px-2 py-1 rounded-sm flex items-center gap-1">
              <CheckCircle size={10} /><span className="text-xs font-medium">Verified</span>
            </div>
          )}
          <div className="absolute bottom-3 left-3 bg-black/70 backdrop-blur-sm px-2 py-1 rounded-sm flex items-center gap-1">
            <GrowTypeIcon size={12} className="text-green-400" />
            <span className="text-xs capitalize">{grower.grow_type}</span>
          </div>
        </div>
        <div className="p-4">
          <h3 className="font-heading font-bold text-lg mb-1 truncate">{grower.business_name}</h3>
          <div className="flex items-center gap-1 text-sm text-muted-foreground mb-2">
            <MapPin size={14} /><span className="truncate">{grower.city}, Minnesota</span>
          </div>
          {/* Ownership badges */}
          {(grower.women_owned || grower.native_owned || grower.bipoc_owned) && (
            <div className="mb-2">
              <OwnershipBadges 
                womenOwned={grower.women_owned} 
                nativeOwned={grower.native_owned} 
                bipocOwned={grower.bipoc_owned}
                size="small"
              />
            </div>
          )}
          <div className="flex flex-wrap gap-1.5 mb-3">
            {grower.accepts_wholesale && <Badge variant="outline" className="text-xs px-1.5 py-0"><Package size={10} className="mr-1" /> Wholesale</Badge>}
            {grower.offers_clones && <Badge variant="outline" className="text-xs px-1.5 py-0"><Scissors size={10} className="mr-1" /> Clones</Badge>}
            {grower.offers_seeds && <Badge variant="outline" className="text-xs px-1.5 py-0"><Sprout size={10} className="mr-1" /> Seeds</Badge>}
          </div>
          {grower.description && <p className="text-sm text-muted-foreground line-clamp-2">{grower.description}</p>}
          {grower.specialties && <p className="text-xs text-green-400 mt-2 truncate">{grower.specialties}</p>}
        </div>
      </motion.div>
    </Link>
  );
};

// Extractor Card Component
const ExtractorCard = ({ extractor, featured = false }) => (
  <Link to={`/extractor/${extractor.id}`} data-testid={`extractor-card-${extractor.id}`}>
    <motion.div
      whileHover={{ scale: 1.02 }}
      className={`group relative rounded-sm overflow-hidden bg-card border ${
        featured ? 'border-purple-500/50 ring-1 ring-purple-500/30' : 'border-border'
      } card-hover cursor-pointer`}
    >
      <div className="aspect-video relative bg-purple-900/20">
        {extractor.image_url ? (
          <img 
            src={extractor.image_url} 
            alt={extractor.business_name} 
            className="w-full h-full object-cover"
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
        ) : null}
        <div 
          className={`w-full h-full items-center justify-center bg-gradient-to-br from-purple-900/30 to-purple-600/10 ${extractor.image_url ? 'hidden' : 'flex'}`}
          style={{ display: extractor.image_url ? 'none' : 'flex' }}
        >
          <FlaskConical size={48} className="text-purple-500/50" />
        </div>
        {featured && (
          <div className="absolute top-3 right-3 bg-purple-500 text-white px-2 py-1 rounded-sm">
            <span className="font-bold text-xs">FEATURED</span>
          </div>
        )}
        {extractor.is_verified && (
          <div className="absolute top-3 left-3 bg-blue-500/90 text-white px-2 py-1 rounded-sm flex items-center gap-1">
            <CheckCircle size={10} /><span className="text-xs font-medium">Verified</span>
          </div>
        )}
        <div className="absolute bottom-3 left-3 bg-black/70 backdrop-blur-sm px-2 py-1 rounded-sm flex items-center gap-1">
          <Droplets size={12} className="text-purple-400" />
          <span className="text-xs">{extractor.extraction_types?.split(',')[0]?.trim()}</span>
        </div>
      </div>
      <div className="p-4">
        <h3 className="font-heading font-bold text-lg mb-1 truncate">{extractor.business_name}</h3>
        <div className="flex items-center gap-1 text-sm text-muted-foreground mb-2">
          <MapPin size={14} /><span className="truncate">{extractor.city}, Minnesota</span>
        </div>
        {/* Ownership badges */}
        {(extractor.women_owned || extractor.native_owned || extractor.bipoc_owned) && (
          <div className="mb-2">
            <OwnershipBadges 
              womenOwned={extractor.women_owned} 
              nativeOwned={extractor.native_owned} 
              bipocOwned={extractor.bipoc_owned}
              size="small"
            />
          </div>
        )}
        <div className="flex flex-wrap gap-1.5 mb-3">
          {extractor.accepts_wholesale && <Badge variant="outline" className="text-xs px-1.5 py-0"><Package size={10} className="mr-1" /> Wholesale</Badge>}
          {extractor.white_label && <Badge variant="outline" className="text-xs px-1.5 py-0"><Tag size={10} className="mr-1" /> White Label</Badge>}
        </div>
        {extractor.description && <p className="text-sm text-muted-foreground line-clamp-2">{extractor.description}</p>}
        {extractor.products && <p className="text-xs text-purple-400 mt-2 truncate">{extractor.products}</p>}
      </div>
    </motion.div>
  </Link>
);

export default CannaClub;
